import { useEffect, useState } from 'react';
import { AdminLayout } from '../../components/admin-layout';
import { Card, Table, Th, Td, Input, Select, Pagination, Alert, Badge } from '../../components/ui';
import { getLaporanAbsensi, getPeriodeList } from '../../features/absensi/api';
import { getSekolahList, getPegawaiList } from '../../features/master/api';
import { RoleCode, type Periode, type Sekolah, type PegawaiListItem } from '@app/shared';
import { useAuth } from '../../features/auth/use-auth';

type LaporanRow = {
  pegawaiId: number;
  pegawaiSekolahId: number;
  nama: string;
  nipNik: string;
  sekolahNama: string;
  sekolahId: number;
  hadir: number;
  izin: number;
  sakit: number;
  alpha: number;
  totalHariKerja: number;
  persentaseKehadiran: number;
};

export function LaporanAbsensiPage() {
  const { user } = useAuth();
  const isAdmin = user?.roles.includes(RoleCode.ADMIN) ?? false;
  const isKS = user?.roles.includes(RoleCode.KEPALA_SEKOLAH) ?? false;

  const [rows, setRows] = useState<LaporanRow[]>([]);
  const [meta, setMeta] = useState({ page: 1, limit: 20, total: 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Filter states
  const [periodeId, setPeriodeId] = useState<number | null>(null);
  const [sekolahId, setSekolahId] = useState<number | null>(null);
  const [pegawaiId, setPegawaiId] = useState<number | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState<
    'nama' | 'nipNik' | 'sekolahNama' | 'hadir' | 'persentaseKehadiran'
  >('nama');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');

  // Dropdown data
  const [periodes, setPeriodes] = useState<Periode[]>([]);
  const [sekolahs, setSekolahs] = useState<Sekolah[]>([]);
  const [pegawais, setPegawais] = useState<PegawaiListItem[]>([]);

  // Load dropdowns
  useEffect(() => {
    const loadDropdowns = async () => {
      try {
        const [periodeResult, sekolahResult, pegawaiResult] = await Promise.all([
          getPeriodeList({ limit: 100 }),
          getSekolahList({ limit: 100, status_aktif: true }),
          getPegawaiList({ limit: 500, status_aktif: true }),
        ]);
        setPeriodes(periodeResult.rows);
        setSekolahs(sekolahResult.rows);
        setPegawais(pegawaiResult.rows);

        // Auto-select first active periode
        const aktivePeriode = periodeResult.rows.find((p: Periode) => p.status === 'aktif');
        if (aktivePeriode) {
          setPeriodeId(aktivePeriode.id);
        } else if (periodeResult.rows.length > 0 && periodeResult.rows[0]) {
          setPeriodeId(periodeResult.rows[0].id);
        }
      } catch (e) {
        console.error('Failed to load dropdowns:', e);
      }
    };
    loadDropdowns();
  }, []);

  const load = async (page = 1) => {
    if (!periodeId) return;

    setLoading(true);
    setError(null);
    try {
      const result = await getLaporanAbsensi({
        periodeId,
        sekolahId: sekolahId ?? undefined,
        pegawaiId: pegawaiId ?? undefined,
        status: statusFilter || undefined,
        page,
        limit: 20,
        search: search || undefined,
        sort_by: sortBy,
        sort_dir: sortDir,
      });
      setRows(result.rows);
      setMeta({ page: result.page, limit: result.limit, total: result.total });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memuat data laporan');
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (periodeId) {
      load(1);
    }
  }, [periodeId, sekolahId, pegawaiId, statusFilter, search, sortBy, sortDir]);

  const formatPersentase = (value: number) => {
    return `${value.toFixed(2)}%`;
  };

  const getPersentaseBadge = (persentase: number) => {
    if (persentase >= 90) return <Badge color="green">{formatPersentase(persentase)}</Badge>;
    if (persentase >= 75) return <Badge color="blue">{formatPersentase(persentase)}</Badge>;
    if (persentase >= 60) return <Badge color="gray">{formatPersentase(persentase)}</Badge>;
    return <Badge color="red">{formatPersentase(persentase)}</Badge>;
  };

  return (
    <AdminLayout title="Laporan Absensi">
      <Card>
        <div className="mb-6">
          <h2 className="text-xl font-semibold mb-4">Filter Laporan</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">Periode *</label>
              <Select
                value={periodeId?.toString() ?? ''}
                onChange={(e) => setPeriodeId(Number(e.target.value) || null)}
                disabled={loading}
              >
                <option value="">Pilih Periode</option>
                {periodes.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.nama} ({p.kode_periode})
                  </option>
                ))}
              </Select>
            </div>

            {(isAdmin || isKS) && (
              <div>
                <label className="block text-sm font-medium mb-1">Sekolah</label>
                <Select
                  value={sekolahId?.toString() ?? ''}
                  onChange={(e) => setSekolahId(Number(e.target.value) || null)}
                  disabled={loading}
                >
                  <option value="">Semua Sekolah</option>
                  {sekolahs.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.nama}
                    </option>
                  ))}
                </Select>
              </div>
            )}

            {(isAdmin || isKS) && (
              <div>
                <label className="block text-sm font-medium mb-1">Pegawai</label>
                <Select
                  value={pegawaiId?.toString() ?? ''}
                  onChange={(e) => setPegawaiId(Number(e.target.value) || null)}
                  disabled={loading}
                >
                  <option value="">Semua Pegawai</option>
                  {pegawais.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.nama} ({p.nip_nik})
                    </option>
                  ))}
                </Select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium mb-1">Status Kehadiran</label>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                disabled={loading}
              >
                <option value="">Semua Status</option>
                <option value="hadir">Hadir</option>
                <option value="izin">Izin</option>
                <option value="sakit">Sakit</option>
                <option value="alpha">Alpha</option>
              </Select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Cari Pegawai</label>
              <Input
                type="text"
                placeholder="Nama / NIP/NIK..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                disabled={loading}
              />
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Urutkan Berdasarkan</label>
              <div className="flex gap-2">
                <Select
                  value={sortBy}
                  onChange={(e) =>
                    setSortBy(
                      e.target.value as
                        'nama' | 'nipNik' | 'sekolahNama' | 'hadir' | 'persentaseKehadiran',
                    )
                  }
                  disabled={loading}
                  className="flex-1"
                >
                  <option value="nama">Nama</option>
                  <option value="nipNik">NIP/NIK</option>
                  <option value="sekolahNama">Sekolah</option>
                  <option value="hadir">Jumlah Hadir</option>
                  <option value="persentaseKehadiran">Persentase</option>
                </Select>
                <Select
                  value={sortDir}
                  onChange={(e) => setSortDir(e.target.value as 'asc' | 'desc')}
                  disabled={loading}
                  className="w-24"
                >
                  <option value="asc">A-Z</option>
                  <option value="desc">Z-A</option>
                </Select>
              </div>
            </div>
          </div>
        </div>

        {error && (
          <div style={{ marginBottom: '1rem' }}>
            <Alert type="error">{error}</Alert>
          </div>
        )}

        {!periodeId && !loading && (
          <div style={{ marginBottom: '1rem' }}>
            <Alert type="info">Silakan pilih periode untuk melihat laporan absensi</Alert>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12 text-gray-500">Loading...</div>
        ) : rows.length === 0 && periodeId ? (
          <div className="text-center py-12 text-gray-500">
            <p>Tidak ada data laporan absensi</p>
            <p className="text-sm mt-2">Coba ubah filter atau periode yang dipilih</p>
          </div>
        ) : rows.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <Table>
                <thead>
                  <tr>
                    <Th>No</Th>
                    <Th>NIP/NIK</Th>
                    <Th>Nama Pegawai</Th>
                    {(isAdmin || isKS) && <Th>Sekolah</Th>}
                    <Th style={{ textAlign: 'center' }}>Hadir</Th>
                    <Th style={{ textAlign: 'center' }}>Izin</Th>
                    <Th style={{ textAlign: 'center' }}>Sakit</Th>
                    <Th style={{ textAlign: 'center' }}>Alpha</Th>
                    <Th style={{ textAlign: 'center' }}>Hari Kerja</Th>
                    <Th style={{ textAlign: 'center' }}>Persentase</Th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, idx) => (
                    <tr key={row.pegawaiSekolahId}>
                      <Td>{(meta.page - 1) * meta.limit + idx + 1}</Td>
                      <Td className="font-mono text-sm">{row.nipNik}</Td>
                      <Td className="font-medium">{row.nama}</Td>
                      {(isAdmin || isKS) && (
                        <Td className="text-sm text-gray-600">{row.sekolahNama}</Td>
                      )}
                      <Td className="text-center">
                        <Badge color="green">{row.hadir}</Badge>
                      </Td>
                      <Td className="text-center">
                        <Badge color="blue">{row.izin}</Badge>
                      </Td>
                      <Td className="text-center">
                        <Badge color="gray">{row.sakit}</Badge>
                      </Td>
                      <Td className="text-center">
                        <Badge color="red">{row.alpha}</Badge>
                      </Td>
                      <Td className="text-center text-gray-600">{row.totalHariKerja}</Td>
                      <Td className="text-center">{getPersentaseBadge(row.persentaseKehadiran)}</Td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>

            <div className="mt-4 flex justify-between items-center">
              <p className="text-sm text-gray-600">
                Menampilkan {(meta.page - 1) * meta.limit + 1} -{' '}
                {Math.min(meta.page * meta.limit, meta.total)} dari {meta.total} data
              </p>
              <Pagination
                page={meta.page}
                totalPages={Math.ceil(meta.total / meta.limit)}
                onChange={load}
              />
            </div>
          </>
        ) : null}
      </Card>
    </AdminLayout>
  );
}
